// components/create/RSVPSection.js
import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/RSVPSection.scss';

// Import modals
import PrivacySelector from './PrivacySelector';
import EmojiSetSelector from './EmojiSetSelector';
import GuestListSelector from './GuestListSelector';

// Services
import { getUserGuestLists } from '../../services/guestListService';

export default function RSVPSection({
  privacy,
  setPrivacy,
  rsvpDeadline,
  setRsvpDeadline,
  maxAttendees,
  setMaxAttendees,
  plusOnesAllowed,
  setPlusOnesAllowed,
  hideMaybeRsvp,
  setHideMaybeRsvp,
  enableWaitlist,
  setEnableWaitlist,
  hideLocationBeforeVerification,
  setHideLocationBeforeVerification,
  emojiSet,
  setEmojiSet,
  customEmojis,
  setCustomEmojis,
  guestListIds = [],
  setGuestListIds,
  showInExploreFeed = false,  // NEW: for private events
  setShowInExploreFeed,        // NEW: for private events
  errors = {},
}) {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showEmojiModal, setShowEmojiModal] = useState(false);
  const [showGuestListModal, setShowGuestListModal] = useState(false);
  const [limitSpots, setLimitSpots] = useState(!!maxAttendees);
  const [guestLists, setGuestLists] = useState([]);

  // Load guest lists when privacy is private or when guestListIds change
  useEffect(() => {
    if (privacy === 'private') {
      loadSelectedGuestLists();
    }
  }, [privacy, guestListIds]);

  const loadSelectedGuestLists = async () => {
    try {
      if (guestListIds.length === 0) {
        setGuestLists([]);
        return;
      }
      
      const allLists = await getUserGuestLists();
      const selected = allLists.filter(list => guestListIds.includes(list.id));
      console.log('📋 Selected guest lists in RSVP Section:', selected.length);
      setGuestLists(selected);
    } catch (error) {
      console.error('Error loading guest lists:', error);
    }
  };

  const handleGuestListsSelected = (selectedIds) => {
    setGuestListIds(selectedIds);
    setShowGuestListModal(false);
  };

  const handleRemoveGuestList = (listId) => {
    const updatedIds = guestListIds.filter(id => id !== listId);
    setGuestListIds(updatedIds);
  };

  const handleCreateGuestList = () => {
    setShowGuestListModal(false);
    // Navigate to guest lists page
    window.location.href = '/guest-lists';
  };

  const formatDate = (date) => {
    if (!date) return 'Select deadline';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPrivacyLabel = () => {
    switch (privacy) {
      case 'public':
        return 'Public';
      case 'private':
        return 'Private';
      case 'approval':
        return 'Request Approval';
      default:
        return 'Select privacy';
    }
  };

  const getPrivacyDescription = () => {
    switch (privacy) {
      case 'public':
        return 'Anyone can see and RSVP';
      case 'private':
        return 'Only invited guests can see';
      case 'approval':
        return 'Guests must request to attend';
      default:
        return '';
    }
  };

  return (
    <div className="rsvp-section">
      {/* Privacy Settings */}
      <div className="section-card">
        <label className="section-title">Privacy Settings</label>
        
        <button
          type="button"
          className="select-button"
          onClick={() => setShowPrivacyModal(true)}
        >
          <i className={`icon-${privacy === 'public' ? 'globe' : 'lock-closed'}-outline`}></i>
          <div className="select-content">
            <span className="select-text">{getPrivacyLabel()}</span>
            <span className="select-subtext">{getPrivacyDescription()}</span>
          </div>
          <i className="icon-chevron-forward"></i>
        </button>

        {/* NEW: Display in Explore Feed Toggle (Private Events Only) */}
        {privacy === 'private' && (
          <div className="toggle-row">
            <div className="toggle-content">
              <p className="toggle-label">Display invitation in explore feed</p>
              <p className="toggle-description">
                Show this private event in explore for better reach.
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={showInExploreFeed}
                onChange={(e) => setShowInExploreFeed(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        )}

        {/* Hide location toggle for private/approval events */}
        {(privacy === 'private' || privacy === 'approval') && (
          <div className="toggle-row">
            <div className="toggle-content">
              <p className="toggle-label">Hide location until verified</p>
              <p className="toggle-description">
                {privacy === 'private'
                  ? 'Guests must verify phone to see exact location'
                  : 'Location will be shown only after request approval'}
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={hideLocationBeforeVerification}
                onChange={(e) => setHideLocationBeforeVerification(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        )}
      </div>

      {/* RSVP Deadline */}
      <div className="section-card">
        <label className="section-title">RSVP Deadline *</label>
        <DatePicker
          selected={rsvpDeadline}
          onChange={(date) => setRsvpDeadline(date)}
          dateFormat="MMM dd, yyyy"
          placeholderText="Select deadline"
          className={`datetime-input ${errors.rsvpDeadline ? 'error' : ''}`}
          wrapperClassName="datetime-wrapper full-width"
          minDate={new Date()}
          maxDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
        />
        {errors.rsvpDeadline && <p className="error-text">{errors.rsvpDeadline}</p>}
      </div>

      {/* Attendee Limits */}
      <div className="section-card">
        <label className="section-title">Attendee Limits</label>
        
        <div className="toggle-row">
          <p className="toggle-label">Limit number of spots</p>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={limitSpots}
              onChange={(e) => {
                setLimitSpots(e.target.checked);
                if (!e.target.checked) setMaxAttendees(null);
              }}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {limitSpots && (
          <>
            <input
              type="number"
              className="text-input"
              placeholder="Maximum attendees"
              value={maxAttendees || ''}
              onChange={(e) => setMaxAttendees(parseInt(e.target.value) || null)}
              min="1"
            />

            <div className="toggle-row">
              <div className="toggle-content">
                <p className="toggle-label">Enable waiting list</p>
                <p className="toggle-description">
                  Auto-add guests when spots open
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={enableWaitlist}
                  onChange={(e) => setEnableWaitlist(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </>
        )}

        {/* Plus-ones counter */}
        <div className="counter-row">
          <p className="counter-label">Plus-ones allowed</p>
          <div className="counter">
            <button
              type="button"
              className="counter-button"
              onClick={() => setPlusOnesAllowed(Math.max(0, plusOnesAllowed - 1))}
            >
              <i className="icon-remove"></i>
            </button>
            <span className="counter-value">{plusOnesAllowed}</span>
            <button
              type="button"
              className="counter-button"
              onClick={() => setPlusOnesAllowed(Math.min(9, plusOnesAllowed + 1))}
            >
              <i className="icon-add"></i>
            </button>
          </div>
        </div>
      </div>

      {/* RSVP Options */}
      <div className="section-card">
        <label className="section-title">RSVP Options</label>
        
        <div className="toggle-row">
          <p className="toggle-label">Hide "Maybe" option</p>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={hideMaybeRsvp}
              onChange={(e) => setHideMaybeRsvp(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <label className="field-label">RSVP Button Style</label>
        <button
          type="button"
          className="select-button"
          onClick={() => setShowEmojiModal(true)}
        >
          <span className="emoji-preview">
            {emojiSet === 'custom'
              ? `${customEmojis.going} ${customEmojis.maybe} ${customEmojis.notGoing}`
              : 'Default style'}
          </span>
          <i className="icon-chevron-forward"></i>
        </button>
      </div>

      {/* Guest Lists (Private events only) */}
      {privacy === 'private' && (
        <div className="section-card">
          <div className="section-header">
            <label className="section-title">Guest Lists</label>
            <button
              type="button"
              className="manage-button"
              onClick={() => window.location.href = '/guest-lists'}
            >
              <span>Manage</span>
              <i className="icon-chevron-forward"></i>
            </button>
          </div>
          <p className="section-description">
            Select guest lists to invite automatically
          </p>

          {guestLists.length === 0 ? (
            <div className="empty-guest-lists">
              <i className="icon-people-outline"></i>
              <p className="empty-text">No guest lists selected</p>
              <p className="empty-subtext">
                Add guest lists to automatically invite contacts
              </p>
              <button
                type="button"
                className="add-button"
                onClick={() => setShowGuestListModal(true)}
              >
                <i className="icon-add-circle"></i>
                <span>Select Guest Lists</span>
              </button>
            </div>
          ) : (
            <>
              <div className="guest-lists-container">
                {guestLists.map((list) => (
                  <div key={list.id} className="guest-list-item">
                    <div className="guest-list-left">
                      <div className="guest-list-icon">
                        <i className="icon-people"></i>
                      </div>
                      <div className="guest-list-info">
                        <p className="guest-list-name">{list.name}</p>
                        <p className="guest-list-count">
                          {list.contact_count || 0} {(list.contact_count || 0) === 1 ? 'contact' : 'contacts'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => handleRemoveGuestList(list.id)}
                    >
                      <i className="icon-close-circle"></i>
                    </button>
                  </div>
                ))}
              </div>
              
              <button
                type="button"
                className="add-button secondary"
                onClick={() => setShowGuestListModal(true)}
              >
                <i className="icon-add-circle"></i>
                <span>Add More Guest Lists</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Modals */}
      {showPrivacyModal && (
        <PrivacySelector
          visible={showPrivacyModal}
          onClose={() => setShowPrivacyModal(false)}
          selectedPrivacy={privacy}
          onSelect={(value) => {
            setPrivacy(value);
            setShowPrivacyModal(false);
            // Reset show in explore feed when changing from private
            if (value !== 'private') {
              setShowInExploreFeed(false);
            }
          }}
        />
      )}

      {showEmojiModal && (
        <EmojiSetSelector
          visible={showEmojiModal}
          onClose={() => setShowEmojiModal(false)}
          selectedSet={emojiSet}
          customEmojis={customEmojis}
          onSelect={(set, emojis) => {
            setEmojiSet(set);
            if (emojis) setCustomEmojis(emojis);
            setShowEmojiModal(false);
          }}
        />
      )}

      {showGuestListModal && (
        <GuestListSelector
          visible={showGuestListModal}
          onClose={() => setShowGuestListModal(false)}
          selectedGuestListIds={guestListIds}
          onGuestListsSelected={handleGuestListsSelected}
          onCreateGuestList={handleCreateGuestList}
        />
      )}
    </div>
  );
}